/**
 * ArdourSessionFileXMLParser
 */

import { SessionData, Route, Source, Location, Region, Playlist } from '@shared/types/session';

/**
 * XMLstringSessionDataConvert
 */
export function parseSessionXML(xmlString: string): SessionData | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');
    
 // Error
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      console.error('XML parse error:', parseError.textContent);
      return null;
    }
    
    const sessionElement = doc.documentElement;
    if (sessionElement.nodeName !== 'Session') {
      console.error('Root element is not Session');
      return null;
    }
    
    const session: SessionData = {
      version: sessionElement.getAttribute('version') || '',
      name: sessionElement.getAttribute('name') || '',
      sampleRate: parseInt(sessionElement.getAttribute('sample-rate') || '48000', 10),
    };
    
    // ProgramVersion
    const programVersionEl = sessionElement.querySelector('ProgramVersion');
    if (programVersionEl) {
      session.programVersion = {
        createdWith: programVersionEl.getAttribute('created-with') || '',
        modifiedWith: programVersionEl.getAttribute('modified-with') || undefined,
      };
    }
    
    // Routes
    const routesEl = sessionElement.querySelector('Routes');
    if (routesEl) {
      session.routes = parseRoutes(routesEl);
    }
    
    // Sources
    const sourcesEl = sessionElement.querySelector('Sources');
    if (sourcesEl) {
      session.sources = parseSources(sourcesEl);
    }
    
    // Locations
    const locationsEl = sessionElement.querySelector('Locations');
    if (locationsEl) {
      session.locations = parseLocations(locationsEl);
    }
    
    // Regions
    const regionsEl = sessionElement.querySelector('Regions');
    if (regionsEl) {
      session.regions = parseRegions(regionsEl);
    }
    
    // Playlists
    const playlistsEl = sessionElement.querySelector('Playlists');
    if (playlistsEl) {
      session.playlists = parsePlaylists(playlistsEl);
    }
    
    return session;
  } catch (error) {
    console.error('Error parsing session XML:', error);
    return null;
  }
}

function parseRoutes(routesElement: Element): Route[] {
  const routes: Route[] = [];
  const routeElements = routesElement.querySelectorAll('Route');
  
  routeElements.forEach((routeEl) => {
    const route: Route = {
      id: routeEl.getAttribute('id') || '',
      name: routeEl.getAttribute('name') || '',
      type: (routeEl.getAttribute('type') as Route['type']) || 'AudioTrack',
    };
    
    // Inputs
    const inputsEl = routeEl.querySelector('Input');
    if (inputsEl) {
      route.inputs = {
        n_audio: parseInt(inputsEl.getAttribute('audio') || '0', 10),
        n_midi: parseInt(inputsEl.getAttribute('midi') || '0', 10),
      };
    }
    
    // Outputs
    const outputsEl = routeEl.querySelector('Output');
    if (outputsEl) {
      route.outputs = {
        n_audio: parseInt(outputsEl.getAttribute('audio') || '0', 10),
        n_midi: parseInt(outputsEl.getAttribute('midi') || '0', 10),
      };
    }
    
    routes.push(route);
  });
  
  return routes;
}

function parseSources(sourcesElement: Element): Source[] {
  const sources: Source[] = [];
  const sourceElements = sourcesElement.querySelectorAll('Source');
  
  sourceElements.forEach((sourceEl) => {
    const source: Source = {
      id: sourceEl.getAttribute('id') || '',
      name: sourceEl.getAttribute('name') || '',
      type: (sourceEl.getAttribute('type') as Source['type']) || 'audio',
    };
    
    const path = sourceEl.getAttribute('path');
    if (path) source.path = path;
    
    const length = sourceEl.getAttribute('length');
    if (length) source.length = parseInt(length, 10);
    
    const channels = sourceEl.getAttribute('channels');
    if (channels) source.channels = parseInt(channels, 10);
    
    const sampleRate = sourceEl.getAttribute('sample-rate');
    if (sampleRate) source.sampleRate = parseInt(sampleRate, 10);
    
    sources.push(source);
  });
  
  return sources;
}

function parseLocations(locationsElement: Element): Location[] {
  const locations: Location[] = [];
  const locationElements = locationsElement.querySelectorAll('Location');
  
  locationElements.forEach((locationEl) => {
    const location: Location = {
      id: locationEl.getAttribute('id') || '',
      name: locationEl.getAttribute('name') || '',
      start: parseInt(locationEl.getAttribute('start') || '0', 10),
      flags: parseInt(locationEl.getAttribute('flags') || '0', 10),
    };
    
    const end = locationEl.getAttribute('end');
    if (end) location.end = parseInt(end, 10);
    
    locations.push(location);
  });
  
  return locations;
}

function parseRegions(regionsElement: Element): Region[] {
  const regions: Region[] = [];
  const regionElements = regionsElement.querySelectorAll('Region');
  
  regionElements.forEach((regionEl) => {
    const region: Region = {
      id: regionEl.getAttribute('id') || '',
      name: regionEl.getAttribute('name') || '',
      type: (regionEl.getAttribute('type') as Region['type']) || 'audio',
      start: parseInt(regionEl.getAttribute('start') || '0', 10),
      length: parseInt(regionEl.getAttribute('length') || '0', 10),
      position: parseInt(regionEl.getAttribute('position') || '0', 10),
    };
    
    const sourceId = regionEl.getAttribute('source');
    if (sourceId) region.sourceId = sourceId;
    
    const muted = regionEl.getAttribute('muted');
    if (muted === '1' || muted === 'true') region.muted = true;
    
    regions.push(region);
  });
  
  return regions;
}

function parsePlaylists(playlistsElement: Element): Playlist[] {
  const playlists: Playlist[] = [];
  const playlistElements = playlistsElement.querySelectorAll('Playlist');
  
  playlistElements.forEach((playlistEl) => {
    const playlist: Playlist = {
      id: playlistEl.getAttribute('id') || '',
      name: playlistEl.getAttribute('name') || '',
      regions: [],
    };
    
    const regionElements = playlistEl.querySelectorAll('Region');
    regionElements.forEach((regionEl) => {
      playlist.regions.push({
        id: regionEl.getAttribute('id') || '',
        position: parseInt(regionEl.getAttribute('position') || '0', 10),
        start: parseInt(regionEl.getAttribute('start') || '0', 10),
        length: parseInt(regionEl.getAttribute('length') || '0', 10),
        muted: regionEl.getAttribute('muted') === '1' || regionEl.getAttribute('muted') === 'true',
      });
    });
    
    playlists.push(playlist);
  });
  
  return playlists;
}

/**
 * SessionDataXMLstringConvert
 */
export function serializeSessionXML(session: SessionData): string {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += `<Session version="${session.version}" name="${session.name}" sample-rate="${session.sampleRate}">\n`;
  
  // ProgramVersion
  if (session.programVersion) {
    xml += `  <ProgramVersion created-with="${session.programVersion.createdWith}"`;
    if (session.programVersion.modifiedWith) {
      xml += ` modified-with="${session.programVersion.modifiedWith}"`;
    }
    xml += '/>\n';
  }
  
  // Routes
  if (session.routes && session.routes.length > 0) {
    xml += '  <Routes>\n';
    session.routes.forEach((route) => {
      xml += `    <Route id="${route.id}" name="${route.name}" type="${route.type}">\n`;
      if (route.inputs) {
        xml += `      <Input audio="${route.inputs.n_audio}" midi="${route.inputs.n_midi}"/>\n`;
      }
      if (route.outputs) {
        xml += `      <Output audio="${route.outputs.n_audio}" midi="${route.outputs.n_midi}"/>\n`;
      }
      xml += '    </Route>\n';
    });
    xml += '  </Routes>\n';
  }
  
  // Sources
  if (session.sources && session.sources.length > 0) {
    xml += '  <Sources>\n';
    session.sources.forEach((source) => {
      xml += `    <Source id="${source.id}" name="${source.name}" type="${source.type}"`;
      if (source.path) xml += ` path="${source.path}"`;
      if (source.length !== undefined) xml += ` length="${source.length}"`;
      if (source.channels !== undefined) xml += ` channels="${source.channels}"`;
      if (source.sampleRate !== undefined) xml += ` sample-rate="${source.sampleRate}"`;
      xml += '/>\n';
    });
    xml += '  </Sources>\n';
  }
  
  // Locations
  if (session.locations && session.locations.length > 0) {
    xml += '  <Locations>\n';
    session.locations.forEach((location) => {
      xml += `    <Location id="${location.id}" name="${location.name}" start="${location.start}" flags="${location.flags}"`;
      if (location.end !== undefined) xml += ` end="${location.end}"`;
      xml += '/>\n';
    });
    xml += '  </Locations>\n';
  }
  
  // Regions
  if (session.regions && session.regions.length > 0) {
    xml += '  <Regions>\n';
    session.regions.forEach((region) => {
      xml += `    <Region id="${region.id}" name="${region.name}" type="${region.type}" start="${region.start}" length="${region.length}" position="${region.position}"`;
      if (region.sourceId) xml += ` source="${region.sourceId}"`;
      if (region.muted) xml += ' muted="1"';
      xml += '/>\n';
    });
    xml += '  </Regions>\n';
  }
  
  // Playlists
  if (session.playlists && session.playlists.length > 0) {
    xml += '  <Playlists>\n';
    session.playlists.forEach((playlist) => {
      xml += `    <Playlist id="${playlist.id}" name="${playlist.name}">\n`;
      playlist.regions.forEach((region) => {
        xml += `      <Region id="${region.id}" position="${region.position}" start="${region.start}" length="${region.length}"`;
        if (region.muted) xml += ' muted="1"';
        xml += '/>\n';
      });
      xml += '    </Playlist>\n';
    });
    xml += '  </Playlists>\n';
  }
  
  xml += '</Session>\n';
  return xml;
}

